import { XDMWrapper, TARGETS } from '@gt-common/event-logger-web';
import { RailsBaseSchema } from './railsSchemas';
import {
  generateUUID,
  buildSearchContext,
  getAdvancePurchaseDays,
  sendDualXDMEvent,
  buildEventTrackingContext,
  transformForXDM,
} from './xdmcommonutils';
import { getLocusV2Data } from '../pages/listing/listingStore/ListingActions';
import {
  splitName,
  getGenderValue,
  getReservationType,
  getBerthPrefValue,
  buildDualTravellerInfo,
} from './travellerInfoHelper';
import {
  DEFAULT_CRYPT_SALT,
  LOB,
  LOB_CATEGORY,
  PAGE_TYPE_FUNNEL,
  CURRENCY,
  SKU_TYPE_REGULAR,
  CATALOG_SOURCE,
  STORE_ID,
  INFANT_MAX_AGE,
  CHILD_MAX_AGE,
  TRAVELLER_TYPE,
  EVENT_NAMES,
  EVENT_TYPES,
  PAGE_NAMES,
  COMPONENT_IDS,
  ADDON_TYPES,
} from './xdmConstants';

const LOG_NAMES = {
  TRAVELLER_LOAD: 'TRAVELLER_LOAD',
  BOARDING_POINT_CHANGE: 'BOARDING_POINT_CHANGE',
  FC_OPTED: 'FC_OPTED',
  CHANGE_IRCTC_USERNAME: 'CHANGE_IRCTC_USERNAME',
  GET_NEW_PASSWORD: 'GET_NEW_PASSWORD',
  WHY_IS_THIS_REQUIRED: 'WHY_IS_THIS_REQUIRED',
  APPLY_COUPON: 'APPLY_COUPON',
  REMOVE_COUPON: 'REMOVE_COUPON',
  ADDITIONAL_PREFERENCE_EXPAND: 'ADDITIONAL_PREFERENCE_EXPAND',
  ADDITIONAL_PREFERENCE_VIEW_MORE: 'ADDITIONAL_PREFERENCE_VIEW_MORE',
  ADDITIONAL_PREFERENCE_VIEW_ALL: 'ADDITIONAL_PREFERENCE_VIEW_ALL',
  PAY_AND_BOOK: 'PAY_AND_BOOK',
};

const EVENT_VALUES = {
  FUNNEL_RAILS_TRAVELLERS: 'funnel:rails:travellers',
  REVIEW_CLICKED: 'review_page_pay_now_clicked',
  BOARDING_STATION_CLICKED: 'boarding_station_clicked',
  FREE_CANCELLATION_OPTED: 'free_cancellation_opted',
  FREE_CANCELLATION_DESELECTED: 'free_cancellation_deselected',
  TRAVELLER_COUPON_APPLIED: 'traveller_coupon_applied',
  COUPON_REMOVED: 'coupon_removed',
  IRCTC_USERNAME_CHANGE: 'irctc_username_change',
  GET_NEW_IRCTC_PASSWORD_CLICKED: 'get_new_irctc_password_clicked',
  WHY_IS_THIS_REQUIRED: 'why_is_this_required',
  ADDITIONAL_PREFERENCE_EXPAND: 'additional_preference_expand',
  ADDITIONAL_PREFERENCE_VIEW_MORE: 'additional_preference_view_more',
  ADDITIONAL_PREFERENCE_VIEW_ALL: 'additional_preference_view_all',
};

const getStationData = async (fromStationNode, toStationNode, railInfo) => {
  const urlParams = new URLSearchParams(window.location.search);
  const srcCode = fromStationNode?.code || urlParams.get('from') || railInfo?.train?.frmStnCode || '';
  const destCode = toStationNode?.code || urlParams.get('to') || railInfo?.train?.toStnCode || '';
  const srcCity = fromStationNode?.city || railInfo?.train?.frmStnName || srcCode;
  const destCity = toStationNode?.city || railInfo?.train?.toStnName || destCode;

  const locus = (!fromStationNode?.locusV2Id && srcCode && destCode)
    ? await getLocusV2Data(srcCode, destCode).catch(() => null)
    : null;
  
  const fromStation = {
    code: srcCode, city: srcCity,
    locusCode: fromStationNode?.locusCode || locus?.fromLocusCode || srcCode,
    locusType: fromStationNode?.locusType || locus?.sourceLocusType || '',
    locusV2Id: fromStationNode?.locusV2Id || locus?.sourceLocusV2Id || '',
    locusV2Type: fromStationNode?.locusV2Type || locus?.sourceLocusV2Type || '',
  };
  
  const toStation = {
    code: destCode, city: destCity,
    locusCode: toStationNode?.locusCode || locus?.toLocusCode || destCode,
    locusType: toStationNode?.locusType || locus?.destinationLocusType || '',
    locusV2Id: toStationNode?.locusV2Id || locus?.destinationLocusV2Id || '',
    locusV2Type: toStationNode?.locusV2Type || locus?.destinationLocusV2Type || '',
  };
  
  return { fromStation, toStation, srcCode, destCode, srcCity, destCity };
};

const calculateFromDateTime = (railInfo) => {
  const doj = railInfo?.doj;
  const departureTime = railInfo?.train?.departureTime || '';
  let fromDateTime = Date.now();
  if (doj && departureTime) {
    try {
      const year = doj.substring(0, 4);
      const month = doj.substring(4, 6);
      const day = doj.substring(6, 8);
      const [hours, minutes] = departureTime.split(':');
      fromDateTime = new Date(`${year}-${month}-${day}T${hours}:${minutes}:00`).getTime();
    } catch (e) {
      console.error('Error parsing date:', e);
    }
  }
  return fromDateTime;
};

const getMealPrefValue = (traveller, foodChoiceEnabled) => {
  if (!foodChoiceEnabled) return null;
  return traveller?.mealPref?.id ?? traveller?.mealPref?.text ?? null;
};

const buildPageContext = (overrides = {}) => ({
  page_type: PAGE_TYPE_FUNNEL,
  page_name: PAGE_NAMES.REVIEW,
  funnel_step: PAGE_NAMES.REVIEW,
  lob: LOB,
  lob_category: LOB_CATEGORY,
  travel_store: { store_id: STORE_ID },
  ...overrides,
});

const buildReviewSearchContext = (fromStation, toStation, railInfo, fromDateTime) => ({
  ...buildSearchContext(fromStation, toStation, railInfo?.selectedClass || ''),
  from_date_time: fromDateTime,
  advance_purchase: getAdvancePurchaseDays(railInfo?.doj),
});

const sendXDMEvent = async (payload) => {
  try {
    await XDMWrapper.logEvent({
      payload: transformForXDM(payload),
      schema: RailsBaseSchema,
      otherConfig: { lobName: LOB, adobeKeyConfig: [] },
      target: TARGETS.ALL,
      extraOptions: {
        lob: LOB,
        destinationUrl: typeof window !== 'undefined' ? window.location.href : '',
      },
    });
  } catch (error) {
    console.error('[travellerEvents sendXDMEvent] Error:', error?.message);
  }
};

const sendClickEvent = async ({
  railInfo, fromStationNode, toStationNode,
  eventValue, components, extraPayload = {}, logName,
}) => {
  try {
    const { fromStation, toStation } = await getStationData(fromStationNode, toStationNode, railInfo);
    const fromDateTime = calculateFromDateTime(railInfo);

    await sendXDMEvent({
      page_context: buildPageContext(),
      search_context: buildReviewSearchContext(fromStation, toStation, railInfo, fromDateTime),
      event_detail: {
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_name: EVENT_NAMES.BUTTON_CLICKED,
        event_type: EVENT_TYPES.USER_INTERACTION,
        event_value: eventValue,
        components,
      },
      ...extraPayload,
      event_tracking_context: buildEventTrackingContext(),
    });
  } catch (xdmError) {
    console.error(`${logName} Error:`, xdmError.message);
  }
};

const parseCouponDetails = (couponDetails, couponCode) => ({
  discountAmount: couponDetails?.discountAmount
    || couponDetails?.discount
    || couponDetails?.couponDiscount
    || couponDetails?.discountValue
    || couponDetails?.amount
    || 0,
  description: couponDetails?.description
    || couponDetails?.couponDescription
    || couponDetails?.desc
    || couponDetails?.message
    || '',
  heading: couponDetails?.heading
    || couponDetails?.title
    || couponDetails?.couponTitle
    || couponDetails?.displayName
    || couponCode,
});

const getBoardingStationCode = (railInfo, fallback) =>
  (typeof railInfo?.train?.boardingStation === 'string'
    ? railInfo.train.boardingStation
    : railInfo?.train?.boardingStation?.stationCode) || fallback;

export const trackTravellerPageLoad = async ({
  railInfo,
  freeCancellationResponse,
  confirmationGuaranteeResponseNew,
  freeCancellationSelectedOption,
  railofySelectedOption,
  fromStationNode,
  toStationNode,
  travellers,
  contactEmail,
  contactMobile,
}) => {
  try {
    const { fromStation, toStation, srcCode: srcStn, destCode: destStn, srcCity, destCity } = await getStationData(fromStationNode, toStationNode, railInfo);
    const addonDetails = [];
    const totalFare = Number(railInfo?.availability?.totalFare) || 0;

    if (freeCancellationResponse) {
      const fcOption = freeCancellationResponse?.cancellationOptions?.[0];
      if (fcOption) {
        const fcPremium = Number(fcOption?.insuranceAmount) || 0;
        const refundAmountStr = fcOption?.refundAmount || '';
        const refundParsed = refundAmountStr.split(' ')?.[1]?.split('/')?.[0];
        const refundAmount = refundParsed ? parseInt(refundParsed, 10) : 0;
        const premiumPercentage = totalFare > 0 ? Math.round((fcPremium / totalFare) * 100) : 0;

        addonDetails.push({
          addontype: ADDON_TYPES.FC,
          id: ADDON_TYPES.FC,
          currency: CURRENCY,
          includedunits: 1,
          unitprice: fcPremium,
          unittype: CURRENCY,
          heading: ADDON_TYPES.FC,
          selected: !!freeCancellationSelectedOption,
          available: true,
          is_preselected: false,
          refund_amount: refundAmount,
          premium_percentage: premiumPercentage,
        });
      }
    }

    if (confirmationGuaranteeResponseNew) {
      const tgOption = confirmationGuaranteeResponseNew?.preferenceInfoRevamped?.[0];
      if (tgOption) {
        const tgPremium = Number(tgOption?.premiumPerPerson) || 0;
        const ticketFareText = confirmationGuaranteeResponseNew?.fareInfoRevamped?.[0]?.value?.[0]?.text || '';
        const ticketFare = ticketFareText.length > 2 ? Number(ticketFareText.substring(2)) : 0;
        const refundAmount = ticketFare * 3;
        const premiumPercentage = ticketFare > 0 ? Math.round((tgPremium / ticketFare) * 100) : 0;

        addonDetails.push({
          addontype: ADDON_TYPES.TG,
          id: ADDON_TYPES.TG,
          currency: CURRENCY,
          includedunits: 1,
          unitprice: tgPremium,
          unittype: CURRENCY,
          heading: ADDON_TYPES.TG,
          selected: !!railofySelectedOption,
          available: true,
          is_preselected: false,
          refund_amount: refundAmount,
          premium_percentage: premiumPercentage,
        });
      }
    }

    const fromDateTime = calculateFromDateTime(railInfo);
    const doj = railInfo?.doj || '';
    const journeyId = `${srcStn}-${destStn}-${doj}`;
    const salt = journeyId || DEFAULT_CRYPT_SALT;

    const travellerList = Object.values(travellers || {});
    const foodChoiceEnabled = !!railInfo?.availabilityBookingConfig?.bkgCfg?.foodChoiceEnabled;

    const { plain: travellerInfoPlain, encrypted: travellerInfoEncrypted } = buildDualTravellerInfo(
      travellerList,
      (traveller, index) => {
        const { fname, lname } = splitName(traveller?.name);
        const genderVal = getGenderValue(traveller);
        const berthPref = getBerthPrefValue(traveller);
        const mealPref = getMealPrefValue(traveller, foodChoiceEnabled);
        return {
          fname,
          ...(lname && { lname }),
          gender: genderVal,
          age: traveller?.age || null,
          title: genderVal,
          moblie_com_id: contactMobile || '',
          email_com_id: contactEmail || '',
          email_id: contactEmail || '',
          mob_id: contactMobile || '',
          type: TRAVELLER_TYPE.TRAVELLER_INFO,
          is_primary: index === 0,
          reservation_type: getReservationType(traveller),
          ...(berthPref && { berth_preference: berthPref }),
          ...(mealPref && { meal_preference: mealPref }),
        };
      },
      salt,
    );

    const basePayload = {
      page_context: buildPageContext(),
      search_context: {
        ...buildSearchContext(fromStation, toStation, railInfo?.selectedClass || ''),
        from_date_time: fromDateTime,
        advance_purchase: getAdvancePurchaseDays(railInfo?.doj),
      },
      event_detail: {
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_name: EVENT_NAMES.PAGE_RENDERED,
        event_type: EVENT_TYPES.PAGE,
        event_value: EVENT_VALUES.FUNNEL_RAILS_TRAVELLERS,
        components: {
          id: COMPONENT_IDS.TRAVELLER_PAGE,
          product_list: [{
            id: `${railInfo?.train?.trainNumber}_${srcStn}_${destStn}_${doj}`,
            rank: 0,
            legs: [{
              id: `${railInfo?.train?.trainNumber}_${srcStn}_${destStn}_${doj}`,
              name: railInfo?.train?.trainName || '',
              number: railInfo?.train?.trainNumber || '',
              product_type: 'O',
              duration: railInfo?.train?.duration || 0,
              category: LOB,
              sku_type: SKU_TYPE_REGULAR,
              is_cluster: false,
              is_dynamic_pricing_applicable: false,
              availability_shown: railInfo?.availability?.availablityStatus || '',
              fare_shown: totalFare,
              from_station: {
                code: railInfo?.train?.frmStnCode || srcStn,
                name: railInfo?.train?.frmStnName || srcCity,
                departure_time: railInfo?.train?.departureTime || '',
                departure_date: doj,
              },
              to_station: {
                code: railInfo?.train?.toStnCode || destStn,
                name: railInfo?.train?.toStnName || destCity,
                arrival_time: railInfo?.train?.arrivalTime || '',
                arrival_date: doj,
              },
              boarding_station: {
                code: getBoardingStationCode(railInfo, railInfo?.train?.frmStnCode || srcStn || ''),
                name: railInfo?.train?.frmStnName || srcCity || '',
                departure_time: railInfo?.train?.departureTime || '',
                departure_date: doj,
              },
              catalog_list: [{
                quota: railInfo?.selectedQuota || '',
                class_name: railInfo?.selectedClass || '',
                price: {
                  total_amount: totalFare,
                  base_fare_per_pax: Number(railInfo?.availabilityBookingConfig?.baseFare) || 0,
                  tax_per_pax: Number(railInfo?.availabilityBookingConfig?.serviceTax) || 0,
                  reservation_charge_per_pax: Number(railInfo?.availabilityBookingConfig?.reservationCharge) || 0,
                  super_fast_charge_per_pax: Number(railInfo?.availabilityBookingConfig?.superfastCharge) || 0,
                },
                status: railInfo?.availability?.availablityStatus || '',
                date: railInfo?.availability?.availablityDate || doj,
                availability_last_updated: (() => {
                  const matchingClass = (railInfo?.train?.tbsAvailability || []).find(item => item.className === railInfo?.selectedClass);
                  return matchingClass?.lastUpdatedOn || '';
                })(),
                free_cancellation_available: !!(freeCancellationResponse?.cancellationOptions?.length > 0),
                trip_gaurantee_available: !!(confirmationGuaranteeResponseNew?.preferenceInfoRevamped?.length > 0),
                source_of_data: CATALOG_SOURCE,
              }],
            }],
          }],
          time_spent: 0,
        },
      },
      addon_details: addonDetails,
      event_tracking_context: buildEventTrackingContext(),
    };

    const encryptedPayload = {
      ...basePayload,
      ...(travellerList.length > 0 && { traveller_info: travellerInfoEncrypted }),
    };

    const plainTextPayload = {
      ...basePayload,
      ...(travellerList.length > 0 && { traveller_info: travellerInfoPlain }),
    };

    await sendDualXDMEvent({ encryptedPayload, plainTextPayload });
  } catch (xdmError) {
    console.error(`${LOG_NAMES.TRAVELLER_LOAD} Error:`, xdmError.message);
  }
};

export const trackBoardingPointChangeClicked = async ({
  railInfo, boardingStation, fromStationNode, toStationNode,
}) => {
  try {
    await sendClickEvent({
      railInfo, fromStationNode, toStationNode,
      eventValue: EVENT_VALUES.BOARDING_STATION_CLICKED,
      components: {
        id: 'boarding_point_change',
        boarding_station: {
          code: boardingStation?.stationCode || '',
          name: boardingStation?.stationName || '',
          departure_time: boardingStation?.departureTime || '',
          day_count: boardingStation?.dayCount || 0,
        },
      },
      logName: LOG_NAMES.BOARDING_POINT_CHANGE,
    });
  } catch (error) {
    console.error(`${LOG_NAMES.BOARDING_POINT_CHANGE} Error:`, error?.message);
  }
};

export const trackFCOpted = async ({
  railInfo, freeCancellationResponse, isSelected, fromStationNode, toStationNode,
}) => {
  try {
    const fcOption = freeCancellationResponse?.cancellationOptions?.[0];
    const fcPremiumDetails = freeCancellationResponse?.freeCancellationPremiumDetails;
    const refundAmountStr = fcOption?.refundAmount || '';
    const refundAmountMatch = refundAmountStr.match(/\d+/);
    const refundAmount = refundAmountMatch ? parseInt(refundAmountMatch[0], 10) : 0;

    await sendClickEvent({
      railInfo, fromStationNode, toStationNode,
      eventValue: isSelected ? EVENT_VALUES.FREE_CANCELLATION_OPTED : EVENT_VALUES.FREE_CANCELLATION_DESELECTED,
      components: { id: 'free_cancellation_selection' },
      extraPayload: {
        addon_details: [{
          addontype: ADDON_TYPES.FC,
          id: ADDON_TYPES.FC,
          currency: CURRENCY,
          includedunits: 1,
          unitprice: fcOption?.insuranceAmount || fcPremiumDetails?.insurancePremium || 0,
          unittype: CURRENCY,
          heading: ADDON_TYPES.FC,
          selected: isSelected,
          available: true,
          refund_amount: refundAmount,
          premium_percentage: fcPremiumDetails?.premiumPercentage || 0,
        }],
      },
      logName: LOG_NAMES.FC_OPTED,
    });
  } catch (error) {
    console.error(`${LOG_NAMES.FC_OPTED} Error:`, error?.message);
  }
};

export const trackChangeIRCTCUsernameClicked = async (params) => {
  try { await sendClickEvent({ ...params, eventValue: EVENT_VALUES.IRCTC_USERNAME_CHANGE, components: { id: 'change_irctc_username' }, logName: LOG_NAMES.CHANGE_IRCTC_USERNAME }); }
  catch (e) { console.error(`${LOG_NAMES.CHANGE_IRCTC_USERNAME} Error:`, e?.message); }
};

export const trackGetNewPasswordClicked = async (params) => {
  try { await sendClickEvent({ ...params, eventValue: EVENT_VALUES.GET_NEW_IRCTC_PASSWORD_CLICKED, components: { id: 'get_new_password' }, logName: LOG_NAMES.GET_NEW_PASSWORD }); }
  catch (e) { console.error(`${LOG_NAMES.GET_NEW_PASSWORD} Error:`, e?.message); }
};

export const trackWhyIsThisRequiredClicked = async (params) => {
  try { await sendClickEvent({ ...params, eventValue: EVENT_VALUES.WHY_IS_THIS_REQUIRED, components: { id: 'why_is_this_required' }, logName: LOG_NAMES.WHY_IS_THIS_REQUIRED }); }
  catch (e) { console.error(`${LOG_NAMES.WHY_IS_THIS_REQUIRED} Error:`, e?.message); }
};

const trackCouponEvent = async ({
  railInfo, couponCode, couponDetails, fromStationNode, toStationNode, isApplied,
}) => {
  const { discountAmount, description, heading } = parseCouponDetails(couponDetails, couponCode);

  await sendClickEvent({
    railInfo, fromStationNode, toStationNode,
    eventValue: isApplied ? EVENT_VALUES.TRAVELLER_COUPON_APPLIED : EVENT_VALUES.COUPON_REMOVED,
    components: { id: isApplied ? 'apply_coupon' : 'remove_coupon' },
    extraPayload: {
      addon_details: [{
        addontype: ADDON_TYPES.COUPON,
        id: couponCode,
        currency: CURRENCY,
        includedunits: 1,
        unitprice: Math.abs(discountAmount),
        unittype: CURRENCY,
        heading,
        selected: isApplied,
        available: true,
        is_preselected: false,
        description,
      }],
    },
    logName: isApplied ? LOG_NAMES.APPLY_COUPON : LOG_NAMES.REMOVE_COUPON,
  });
};

export const trackApplyCouponClicked = async (params) => {
  try { await trackCouponEvent({ ...params, isApplied: true }); }
  catch (e) { console.error(`${LOG_NAMES.APPLY_COUPON} Error:`, e?.message); }
};

export const trackRemoveCouponClicked = async (params) => {
  try { await trackCouponEvent({ ...params, isApplied: false }); }
  catch (e) { console.error(`${LOG_NAMES.REMOVE_COUPON} Error:`, e?.message); }
};

export const trackAdditionalPreferenceExpand = async (params) => {
  try { await sendClickEvent({ ...params, eventValue: EVENT_VALUES.ADDITIONAL_PREFERENCE_EXPAND, components: { id: 'additional_preference_expand' }, logName: LOG_NAMES.ADDITIONAL_PREFERENCE_EXPAND }); }
  catch (e) { console.error(`${LOG_NAMES.ADDITIONAL_PREFERENCE_EXPAND} Error:`, e?.message); }
};

export const trackAdditionalPreferenceViewMoreClicked = async (params) => {
  try { await sendClickEvent({ ...params, eventValue: EVENT_VALUES.ADDITIONAL_PREFERENCE_VIEW_MORE, components: { id: 'additional_preference_view_more' }, logName: LOG_NAMES.ADDITIONAL_PREFERENCE_VIEW_MORE }); }
  catch (e) { console.error(`${LOG_NAMES.ADDITIONAL_PREFERENCE_VIEW_MORE} Error:`, e?.message); }
};

export const trackAdditionalPreferenceViewAllClicked = async (params) => {
  try { await sendClickEvent({ ...params, eventValue: EVENT_VALUES.ADDITIONAL_PREFERENCE_VIEW_ALL, components: { id: 'additional_preference_view_all' }, logName: LOG_NAMES.ADDITIONAL_PREFERENCE_VIEW_ALL }); }
  catch (e) { console.error(`${LOG_NAMES.ADDITIONAL_PREFERENCE_VIEW_ALL} Error:`, e?.message); }
};

export const trackPayAndBookClicked = async ({
  railInfo,
  selectedTravellers,
  selectedCoupon,
  freeCancellationResponse,
  confirmationGuaranteeResponseNew,
  freeCancellationSelectedOption,
  railofySelectedOption,
  fromStationNode,
  toStationNode,
  contactEmail,
  contactMobile,
}) => {
  
  try {
    const { srcCode: srcStn, destCode: destStn, srcCity, destCity } = await getStationData(fromStationNode, toStationNode, railInfo);
    const doj = railInfo?.doj || '';
    const fromDateTime = calculateFromDateTime(railInfo);

    const totalCollectibleAmount = Number(railInfo?.availabilityBookingConfig?.totalCollectibleAmount) || 0;
    const discountAmount = Number(selectedCoupon?.discountAmount || selectedCoupon?.discount) || 0;
    const totalFare = Number(railInfo?.availability?.totalFare) || 0;
    const addonDetails = [];

    const fcOption = freeCancellationResponse?.cancellationOptions?.[0];
    const fcAmount = Number(fcOption?.insuranceAmount) || 0;
    const tgOption = confirmationGuaranteeResponseNew?.preferenceInfoRevamped?.[0];
    const tgPremium = Number(tgOption?.premiumPerPerson) || 0;

    const selectedFcCost = (freeCancellationSelectedOption && fcOption) ? fcAmount : 0;
    const selectedTgCost = (railofySelectedOption && tgOption) ? tgPremium : 0;
    const totalWithAddons = totalCollectibleAmount + selectedFcCost + selectedTgCost;

    if (fcOption) {
      const refundAmountStr = fcOption?.refundAmount || '';
      const refundParsed = refundAmountStr.split(' ')?.[1]?.split('/')?.[0];
      const refundAmount = refundParsed ? parseInt(refundParsed, 10) : 0;
      const premiumPercentage = totalFare > 0 ? Math.round((fcAmount / totalFare) * 100) : 0;

      addonDetails.push({
        addontype: ADDON_TYPES.FC,
        id: ADDON_TYPES.FC,
        currency: CURRENCY,
        includedunits: 1,
        unitprice: fcAmount,
        unittype: CURRENCY,
        heading: ADDON_TYPES.FC,
        selected: !!freeCancellationSelectedOption,
        available: true,
        is_preselected: false,
        refund_amount: refundAmount,
        premium_percentage: premiumPercentage,
      });
    }

    if (tgOption) {
      const ticketFareText = confirmationGuaranteeResponseNew?.fareInfoRevamped?.[0]?.value?.[0]?.text || '';
      const ticketFare = ticketFareText.length > 2 ? Number(ticketFareText.substring(2)) : 0;
      const refundAmount = ticketFare * 3;
      const premiumPercentage = ticketFare > 0 ? Math.round((tgPremium / ticketFare) * 100) : 0;

      addonDetails.push({
        addontype: ADDON_TYPES.TG,
        id: ADDON_TYPES.TG,
        currency: CURRENCY,
        includedunits: 1,
        unitprice: tgPremium,
        unittype: CURRENCY,
        heading: ADDON_TYPES.TG,
        selected: !!railofySelectedOption,
        available: true,
        is_preselected: false,
        refund_amount: refundAmount,
        premium_percentage: premiumPercentage,
      });
    }

    if (selectedCoupon?.couponCode) {
      addonDetails.push({
        addontype: ADDON_TYPES.COUPON,
        id: selectedCoupon.couponCode,
        currency: CURRENCY,
        includedunits: 1,
        unitprice: Math.abs(discountAmount),
        unittype: CURRENCY,
        heading: selectedCoupon.couponCode,
        selected: true,
        available: true,
        is_preselected: false,
        discount: Math.abs(discountAmount),
        variant: '0',
      });
    }

    const journeyId = `${srcStn}-${destStn}-${doj}`;
    const salt = journeyId || DEFAULT_CRYPT_SALT;
    const travellerList = Object.values(selectedTravellers || {});
    const foodChoiceEnabled = !!railInfo?.availabilityBookingConfig?.bkgCfg?.foodChoiceEnabled;

    const { plain: travellerInfoPlain, encrypted: travellerInfoEncrypted } = buildDualTravellerInfo(
      travellerList,
      (traveller, index) => {
        const { fname, lname } = splitName(traveller?.name);
        const genderVal = getGenderValue(traveller);
        const berthPref = getBerthPrefValue(traveller);
        const mealPref = getMealPrefValue(traveller, foodChoiceEnabled);
        return {
          fname,
          ...(lname && { lname }),
          gender: genderVal,
          age: traveller?.age || null,
          title: genderVal,
          moblie_com_id: contactMobile || '',
          email_com_id: contactEmail || '',
          email_id: contactEmail || '',
          mob_id: contactMobile || '',
          type: TRAVELLER_TYPE.TRAVELLER_INFO,
          is_primary: index === 0,
          reservation_type: getReservationType(traveller),
          ...(berthPref && { berth_preference: berthPref }),
          ...(mealPref && { meal_preference: mealPref }),
        };
      },
      salt,
    );

    const productList = [{
      id: `${railInfo?.train?.trainNumber}_${srcStn}_${destStn}`,
      train_number: Number(railInfo?.train?.trainNumber) || 0,
      train_name: railInfo?.train?.trainName || '',
      total_fare: totalWithAddons,
      from_station: {
        code: srcStn,
        name: srcCity,
        departure_time: railInfo?.train?.departureTime || '',
        departure_date: new Date(fromDateTime).toString(),
      },
      to_station: {
        code: destStn,
        name: destCity,
        arrival_time: railInfo?.train?.arrivalTime || '',
        arrival_date: new Date(fromDateTime + ((railInfo?.train?.duration || 0) * 60000)).toString(),
      },
      date_of_journey: doj,
      quota: railInfo?.selectedQuota || '',
      class: railInfo?.selectedClass || '',
      boarding_station_selected: getBoardingStationCode(railInfo, srcStn),
      rank: 1,
      price: {
        total_amount: totalWithAddons,
        base_fare_per_pax: Number(railInfo?.availabilityBookingConfig?.baseFare) || 0,
        tax_per_pax: Number(railInfo?.availabilityBookingConfig?.serviceTax) || 0,
        reservation_charge_per_pax: Number(railInfo?.availabilityBookingConfig?.reservationCharge) || 0,
        super_fast_charge_per_pax: Number(railInfo?.availabilityBookingConfig?.superfastCharge) || 0,
        free_cancellation: selectedFcCost,
        ...(selectedCoupon?.couponCode && {
          discounts: {
            applicable_coupons: [{
              code: selectedCoupon.couponCode,
              discount: Math.abs(discountAmount),
              is_applied: true,
            }],
          },
        }),
      },
      legs: [{
        id: `${railInfo?.train?.trainNumber}_${srcStn}_${destStn}_${doj}`,
        name: railInfo?.train?.trainName || '',
        number: railInfo?.train?.trainNumber || '',
        product_type: railInfo?.selectedClass || '',
        duration: railInfo?.train?.duration || 0,
        sku_type: SKU_TYPE_REGULAR,
        from_station: {
          code: srcStn,
          name: srcCity,
          departure_date_time: fromDateTime,
        },
        to_station: {
          code: destStn,
          name: destCity,
          arrival_date_time: fromDateTime + ((railInfo?.train?.duration || 0) * 60000),
        },
        boarding_station: {
          code: getBoardingStationCode(railInfo, srcStn),
          departure_date_time: fromDateTime,
        },
        availability_shown: railInfo?.availability?.availablityStatus || '',
        fare_shown: totalWithAddons,
        is_dynamic_pricing_applicable: false,
        rank: 1,
        catalog_list: [{
          quota: railInfo?.selectedQuota || '',
          class_name: railInfo?.selectedClass || '',
          price: { total_amount: totalWithAddons },
          status: railInfo?.availability?.availablityStatus || '',
          availability_last_updated: 'Updated few mins ago',
          free_cancellation_available: !!(freeCancellationResponse?.cancellationOptions?.length > 0),
          trip_guarantee_available: !!(confirmationGuaranteeResponseNew?.preferenceInfoRevamped?.length > 0),
          source_of_data: CATALOG_SOURCE,
        }],
      }],
    }];

    const basePayload = {
      page_context: buildPageContext({ prev_page_name: PAGE_NAMES.TRAVELLERS }),
      search_context: {
        ...buildSearchContext(
          { code: srcStn, city: srcCity },
          { code: destStn, city: destCity },
          'All',
        ),
        from_date_time: fromDateTime,
        advance_purchase: getAdvancePurchaseDays(doj),
        pax: {
          count: travellerList.length,
          details: {
            adult: {
              count: travellerList.filter(t => (t?.age || 0) >= CHILD_MAX_AGE).length,
            },
            child: {
              count: travellerList.filter(t => { const a = t?.age || 0; return a > INFANT_MAX_AGE && a < CHILD_MAX_AGE; }).length,
              ages: travellerList.filter(t => { const a = t?.age || 0; return a > INFANT_MAX_AGE && a < CHILD_MAX_AGE; }).map(t => String(t?.age || 0)),
            },
            infant: {
              count: travellerList.filter(t => (t?.age || 0) <= INFANT_MAX_AGE && (t?.age || 0) > 0).length,
            },
          },
        },
      },
      event_detail: {
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_name: EVENT_NAMES.BUTTON_CLICKED,
        event_type: EVENT_TYPES.ACTION,
        event_value: EVENT_VALUES.REVIEW_CLICKED,
        interaction_type: 'button_click',
        booking_info: {
          from_date_time: fromDateTime,
          price: totalWithAddons,
          booking_date: new Date().toLocaleDateString('en-US'),
          destination: destCity,
          is_self_booking: false,
          currency: CURRENCY,
          tax: -1,
          ...(selectedCoupon?.couponCode && { applied_coupon: selectedCoupon.couponCode }),
          price_detail: {
            total_discount_amount: Math.abs(discountAmount),
            sell_amount: totalWithAddons,
            cost_amount: totalWithAddons,
            sp_amount: totalWithAddons,
          },
        },
        components: {
          id: COMPONENT_IDS.REVIEW_PAGE,
          product_list: productList,
          time_spent: 0,
        },
      },
      addon_details: addonDetails,
      event_tracking_context: buildEventTrackingContext(),
    };

    const encryptedPayload = {
      ...basePayload,
      traveller_info: travellerInfoEncrypted,
    };

    const plainTextPayload = {
      ...basePayload,
      traveller_info: travellerInfoPlain,
    };

    await sendDualXDMEvent({ encryptedPayload, plainTextPayload });
  } catch (xdmError) {
    console.error(`${LOG_NAMES.PAY_AND_BOOK} Error:`, xdmError.message);
  }
};
